import { Component, input, output, signal } from '@angular/core';
import { DropzoneFile } from '../../../../dropzone/dropzone';
import {
	ACTIVITY_CSV_FIELDS,
	ActivityCsvTemplate,
	CONTENT_CSV_FIELDS,
	ContentCsvTemplate,
	COURSE_CSV_FIELDS,
	CourseCsvTemplate,
	LESSON_CSV_FIELDS,
	LessonCsvTemplate,
	LOCATION_CSV_FIELDS,
	LocationCsvTemplate,
	SCHOOL_ADMIN_TO_LOCATION_MAP_CSV_FIELDS,
	SchoolAdminToLocationMapCsvTemplate,
	SECTION_CSV_FIELDS,
	SectionCsvTemplate,
	SetupWizardPayloadObjectKeys,
	SetupWizardPayload,
	UNIT_CSV_FIELDS,
	UnitCsvTemplate,
	USER_CSV_FIELDS,
	USER_TO_SECTION_MAP_CSV_FIELDS,
	UserCsvTemplate,
	UserToSectionMapCsvTemplate,
	validateActivityCsvRow,
	validateContentCsvRow,
	validateCourseCsvRow,
	validateLessonCsvRow,
	validateLocationCsvRow,
	validateSchoolAdminToLocationCsvRow,
	validateSectionCsvRow,
	validateUnitCsvRow,
	validateUserCsvRow,
	validateUserToSectionCsvRow,
	SetupWizardPayloadObjectKey,
} from '@mira-avant/mtlibs';
import { CsvParseResult, CsvUtil } from '../../../../../../utils/csv.util';
import { NzTableModule } from 'ng-zorro-antd/table';

import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';

export interface SetupWizardCsvFileManagerPendingFile {
	file: DropzoneFile;
	text: string;
	templateType: SetupWizardPayloadObjectKey;
	parseResult: CsvParseResult;
}

@Component({
	selector: 'app-setup-wizard-csv-file-manager',
	imports: [NzTableModule, NzPopconfirmModule],
	standalone: true,
	templateUrl: './setup-wizard-csv-file-manager.html',
	styleUrl: './setup-wizard-csv-file-manager.scss',
})
export class SetupWizardCsvFileManager {
	public onNewFile = input<(file: DropzoneFile) => void>();
	public onApplyPreview = output<SetupWizardPayload>();
	public pending = signal<SetupWizardCsvFileManagerPendingFile[]>([]);

	constructor() {}

	/**
	 * This will convert the pending file to a SetupWizardPayload
	 * file type and output it. The parent will handle merging
	 * it into the main preview.
	 *
	 * @public
	 * @param {SetupWizardCsvFileManagerPendingFile} file
	 */
	public applyPendingFile(file: SetupWizardCsvFileManagerPendingFile) {
		//All of the objects we extract will be added to the 'after' property
		//of the payload's corresponding object type.
		const newPayload: SetupWizardPayload = {
			stats: {
				usersUpserts: 0,
				usersToSectionsUpserts: 0,
				usersToSectionsDeletes: 0,
				locationsCreates: 0,
				locationsUpdates: 0,
				schoolAdminsToLocationsUpserts: 0,
				schoolAdminsToLocationsDeletes: 0,
				sectionsCreates: 0,
				sectionsUpdates: 0,
				coursesCreates: 0,
				coursesUpdates: 0,
				unitsCreates: 0,
				unitsUpdates: 0,
				lessonsCreates: 0,
				lessonsUpdates: 0,
				activitiesCreates: 0,
				activitiesUpdates: 0,
				contentsCreates: 0,
				contentsUpdates: 0,
			},
			payload: {
				users: [],
				usersToSections: [],
				locations: [],
				schoolAdminsToLocations: [],
				sections: [],
				courses: [],
				units: [],
				lessons: [],
				activities: [],
				contents: [],
			},
		};

		switch (file.templateType) {
			case SetupWizardPayloadObjectKeys.users:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.users!.push({ after: obj as UserCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.usersToSections:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.usersToSections!.push({ after: obj as UserToSectionMapCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.locations:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.locations!.push({ after: obj as LocationCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.schoolAdminsToLocations:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.schoolAdminsToLocations!.push({
						after: obj as SchoolAdminToLocationMapCsvTemplate,
					});
				});
				break;
			case SetupWizardPayloadObjectKeys.sections:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.sections!.push({ after: obj as SectionCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.courses:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.courses!.push({ after: obj as CourseCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.units:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.units!.push({ after: obj as UnitCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.lessons:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.lessons!.push({ after: obj as LessonCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.activities:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.activities!.push({ after: obj as ActivityCsvTemplate });
				});
				break;
			case SetupWizardPayloadObjectKeys.contents:
				file.parseResult.success.forEach((obj: unknown) => {
					newPayload.payload.contents!.push({ after: obj as ContentCsvTemplate });
				});
				break;
			default:
				throw new Error('Unrecognized CSV template type during payload conversion');
		}

		this.onApplyPreview.emit(newPayload);

		this.removePendingFile(file);
	}

	public async receiveNewFile(file: DropzoneFile) {
		const blobAsString = await file.data.text();

		console.log(`File dropped: ${file.fileName}, size: ${file.size}, type: ${file.mimeType}`, blobAsString);

		const result: CsvParseResult = await CsvUtil.convertStringToCsv(blobAsString);

		let templateType: SetupWizardPayloadObjectKey;

		const firstLineInFileAsString = blobAsString.split('\n')[0];

		//Auto-detect template type
		if (firstLineInFileAsString === USER_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.users;
		} else if (firstLineInFileAsString === USER_TO_SECTION_MAP_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.usersToSections;
		} else if (firstLineInFileAsString === LOCATION_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.locations;
		} else if (firstLineInFileAsString === SCHOOL_ADMIN_TO_LOCATION_MAP_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.schoolAdminsToLocations;
		} else if (firstLineInFileAsString === SECTION_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.sections;
		} else if (firstLineInFileAsString === COURSE_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.courses;
		} else if (firstLineInFileAsString === UNIT_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.units;
		} else if (firstLineInFileAsString === LESSON_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.lessons;
		} else if (firstLineInFileAsString === ACTIVITY_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.activities;
		} else if (firstLineInFileAsString === CONTENT_CSV_FIELDS.join(',')) {
			templateType = SetupWizardPayloadObjectKeys.contents;
		} else {
			throw new Error('Unrecognized CSV file format');
		}

		//Validate successfully imported rows - this is a deeper validation than the original
		//csv parsing. For example this checks the imported row against the specification to
		//determine if all required fields are present.
		result.success.forEach(row => {
			switch (templateType) {
				case SetupWizardPayloadObjectKeys.users:
					validateUserCsvRow(row as UserCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.usersToSections:
					validateUserToSectionCsvRow(row as UserToSectionMapCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.locations:
					validateLocationCsvRow(row as LocationCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.schoolAdminsToLocations:
					validateSchoolAdminToLocationCsvRow(row as SchoolAdminToLocationMapCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.sections:
					validateSectionCsvRow(row as SectionCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.courses:
					validateCourseCsvRow(row as CourseCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.units:
					validateUnitCsvRow(row as UnitCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.lessons:
					validateLessonCsvRow(row as LessonCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.activities:
					validateActivityCsvRow(row as ActivityCsvTemplate);
					break;
				case SetupWizardPayloadObjectKeys.contents:
					validateContentCsvRow(row as ContentCsvTemplate);
					break;
				default:
					throw new Error('Unrecognized CSV template type during validation');
			}
		});

		this.addPendingFile({
			file,
			text: blobAsString,
			templateType,
			parseResult: result,
		});
	}

	public addPendingFile(file: SetupWizardCsvFileManagerPendingFile) {
		this.pending.update(current => [...current, file]);
	}

	public removePendingFile(file: SetupWizardCsvFileManagerPendingFile) {
		this.pending.update(current => current.filter(f => f !== file));
	}

	public clearPendingFiles() {
		this.pending.set([]);
	}
}
